import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { HeaderNavigation } from '@/components/HeaderNavigation'
import { OTPInput } from '@/components/OTPInput'
import { ResendCountdown } from '@/components/ResendCountdown'
import { useAddPhoneStore } from '@/store/useAddPhoneStore'
import { useUserStore } from '@/store'
import {
  useSendPhoneVerificationCode,
  useVerifyPhoneUpdate,
} from '@/domain/identity'

const CELL_COUNT = 4
const defaultCodes = Array(CELL_COUNT).fill('')

export const AddPhoneVerificationScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [codes, setCodes] = useState<string[]>(defaultCodes)
  const [errorMessage, setErrorMessage] = useState('')

  // Store
  const phone = useAddPhoneStore((state) => state.phone)
  const messagingMethod = useAddPhoneStore((state) => state.messagingMethod)
  const resetAddPhoneStore = useAddPhoneStore((state) => state.reset)
  const userAuthenticated = useUserStore((state) => state.userAuthenticated)
  const setUserAuthenticated = useUserStore(
    (state) => state.setUserAuthenticated,
  )

  // Prepare phone number for API calls
  const fullPhoneNumber = phone
    ? `${phone.countryCode}${phone.number.replace(/\D/g, '')}`
    : ''

  // Subtitle text
  const subtitleText = phone
    ? t('screens:codeVerification.subtitle', {
        method: t('translation:phone'),
        value: `\u202A${phone.countryCode} ${phone.number}\u202C`,
      })
    : t('screens:codeVerification.subtitle', {
        method: t('translation:phone'),
        value: '',
      })

  // API Hooks
  const { mutateAsync: resendVerificationCode, isPending: isSendingCode } =
    useSendPhoneVerificationCode({
      onError: ({ errorMessage }) => {
        if (errorMessage) {
          toast.error(errorMessage)
        }
      },
    })

  const { mutateAsync: verifyCode, isPending: isVerifyingCode } =
    useVerifyPhoneUpdate({
      onSuccess: () => {
        // Update user store with new phone number
        if (userAuthenticated && phone) {
          setUserAuthenticated({
            ...userAuthenticated,
            phone_number: fullPhoneNumber,
            phone_confirmed: true,
          })
        }

        // Reset add phone store
        resetAddPhoneStore()

        // Show success message
        toast.success(t('form:success.phoneNumberAdded'))

        // Navigate to dashboard or next step
        navigate('/dashboard')
      },
      onError: ({ errorMessage, code }) => {
        if (code === 'CODE_INVALID' && errorMessage) {
          setErrorMessage(errorMessage)
          setCodes(defaultCodes)
          return
        }

        if (code === 'TOO_MANY_ATTEMPTS' && errorMessage) {
          setCodes(defaultCodes)
          toast.error(errorMessage)
          return
        }

        setErrorMessage(t('api:validationErrors.invalidCode'))
        setCodes(defaultCodes)
      },
    })

  // Handlers
  const handleResendCode = useCallback(() => {
    if (!fullPhoneNumber || isSendingCode) return

    resendVerificationCode({
      phone: fullPhoneNumber,
      send_method: messagingMethod.code,
    })
  }, [
    fullPhoneNumber,
    messagingMethod.code,
    resendVerificationCode,
    isSendingCode,
  ])

  const handleCodeVerification = useCallback(
    (fullCode: string) => {
      if (isVerifyingCode || !fullPhoneNumber) return

      setErrorMessage('')

      verifyCode({
        phone: fullPhoneNumber,
        code: fullCode,
        new_phone: true,
      })
    },
    [isVerifyingCode, fullPhoneNumber, verifyCode],
  )

  // Redirect if no phone data
  useEffect(() => {
    if (!phone) {
      navigate('/add-phone')
    }
  }, [phone, navigate])

  // Redirect if user already has phone number
  useEffect(() => {
    if (userAuthenticated?.phone_number) {
      navigate('/dashboard')
    }
  }, [userAuthenticated?.phone_number, navigate])

  if (!phone) {
    return null // Will redirect
  }

  return (
    <BaseSafeFormView flex={1} bgColor="background.screen" className="h-full">
      <BaseView flex={1} gap={28}>
        <HeaderNavigation />

        <BaseView gap={20} px={12}>
          <BaseText fontSize={24} fontFamily="semibold" color="label.default">
            {t('screens:codeVerification.title')}
          </BaseText>

          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
          >
            {subtitleText}
          </BaseText>
        </BaseView>

        <BaseView gap={36} center>
          <OTPInput
            autoFocus
            autoFocusAfterClean
            numberCells={4}
            cellStyle={{
              fontSize: 32,
              fontWeight: 600,
            }}
            keyboardType="numeric"
            textContentType="one-time-code"
            variant="filled"
            codes={codes}
            isSubmitting={isSendingCode || isVerifyingCode}
            onSubmit={handleCodeVerification}
            setCodes={setCodes}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />

          <BaseView gap={28} center>
            {/* Messaging method icon/image could be added here */}
            <ResendCountdown onPress={handleResendCode} />
          </BaseView>
        </BaseView>
      </BaseView>
    </BaseSafeFormView>
  )
}

export default AddPhoneVerificationScreen
